import React, { useState } from 'react';
import Icon from './AppIcon';

/**
 * ClientAvatar - Displays a client's avatar with intelligent fallbacks
 * @param {string} src - Avatar image URL
 * @param {string} name - Client name for initials fallback
 * @param {string} className - Additional CSS classes
 * @param {number} size - Size of the avatar (default: 40)
 */
const ClientAvatar = ({ 
  src, 
  name = '', 
  className = '', 
  size = 40,
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);

  // Generate initials from name
  const getInitials = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  // Generate a consistent color based on name
  const getColorFromName = (fullName) => {
    if (!fullName) return 'bg-muted';
    
    // Hash the name to get a consistent color
    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Color palette for avatars (warm, professional colors)
    const colors = [
      'bg-primary',
      'bg-accent',
      'bg-warning',
      'bg-success',
      'bg-secondary',
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500'
    ];
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const initials = getInitials(name);
  const bgColor = getColorFromName(name);
  const sizeStyle = { width: `${size}px`, height: `${size}px` };

  // If there's an image source and no error, try to use it
  if (src && !imageError) {
    return (
      <div 
        className={`rounded-full overflow-hidden flex-shrink-0 bg-muted ${className}`}
        style={sizeStyle}
        {...props}
      >
        <img
          src={src}
          alt={name ? `${name} avatar` : 'Avatar'}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Fallback: Show initials or user icon
  return (
    <div 
      className={`rounded-full ${bgColor} flex items-center justify-center flex-shrink-0 ${className}`}
      style={sizeStyle}
      {...props}
    >
      {initials ? (
        <span 
          className="text-white font-semibold"
          style={{ fontSize: `${size * 0.4}px` }}
        >
          {initials}
        </span>
      ) : (
        <Icon 
          name="User" 
          size={size * 0.5} 
          className="text-white/80"
        />
      )}
    </div>
  );
};

export default ClientAvatar;

